import React, { useCallback, useEffect, useState } from "react";
import {
  useGamePlayTests,
  useGameTopics,
  useMyEntitlements,
  useTopicMessages,
  useUpdateGameTopic,
} from "@seabrookstudios/pitch2table";
import Card from "@mui/material/Card";
import { useGameIdFromRoute, useTopicIdFromRoute } from "../games/WithGameIdFromRoute";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import Skeleton from "@mui/material/Skeleton";
import { formatTopicDate } from "./model";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import { EditableCardHeader } from "../games/EditableHeading";
import { AddMessageToTopic } from "./AddTopic";
import ListItemText from "@mui/material/ListItemText";
import { useMyProfile } from "@seabrookstudios/pitch2table";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Refresh from "@mui/icons-material/Refresh";
import CircularProgress from "@mui/material/CircularProgress";
import { TopicTypeSelect } from "./TopicTypeSelect";
import { useDocumentTitle } from "usehooks-ts";
import { AlwaysOnAppLink } from "../styleguide/AppLink";
import { Path } from "../Path";
import { Message } from "./Message";
import { TopicTypeAdditionalInformation, TopicTypes } from "@seabrookstudios/pitch2table-core";
import Alert from "@mui/material/Alert";
import { CircularProgressSize } from "../theme";
import Stack from "@mui/material/Stack";
import { PrintTopic } from "./PrintTopic";
import { Features } from "../games/features";
import Archive from "@mui/icons-material/Archive";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Key from "@mui/icons-material/Key";
import KeyOff from "@mui/icons-material/KeyOff";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").TopicType} props.type
 */
const TopicTypeBanner = ({ type }) => {
  if (!TopicTypeAdditionalInformation[type]) {
    return;
  }

  return <Alert severity="info">{TopicTypeAdditionalInformation[type]}</Alert>;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").TopicId} props.topicId
 * @param {boolean} props.hideType
 * @param {boolean} props.hideTitle
 * @param {boolean} props.hideDate
 * @param {string} [props.customTitle]
 */
export const TopicDisplay = ({ gameId, topicId, hideType, hideTitle, hideDate, customTitle }) => {
  const { data: entitlements } = useMyEntitlements();
  // Prefer the topics list so a newly created play-test topic appears after GameTopics refetches.
  const { data: topics = [] } = useGameTopics(gameId);
  const topic = topics.find((t) => t.id === topicId);
  const { data: messages = [], refetch, isRefetching } = useTopicMessages(topicId);
  const updateGameTopic = useUpdateGameTopic(gameId, topicId);
  const archiveGameTopic = useUpdateGameTopic(gameId, topicId);
  const { data: profile } = useMyProfile();
  const { data: playTests = [] } = useGamePlayTests(gameId);
  const navigate = useNavigate();
  const [playTest, setPlayTest] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PlayTest|undefined} */ (undefined)
  );
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const playTestForTopic = playTests.find((pt) => pt.topicId === topicId);

    setPlayTest(playTestForTopic);
  }, [playTests, topicId]);

  useDocumentTitle(topic ? topic.name : "Pitch2Table");

  const handleDisabledClick = useCallback(() => {
    if (playTest) {
      enqueueSnackbar("Play test titles are derived from the play test date and cannot be edited.", {
        variant: "info",
      });
    }
  }, [playTest]);

  if (!topicId) {
    return <EmptyDetailCard text="Select a topic from the list on the left." />;
  }

  /**
   * @param {string} name
   */
  const updateTitle = async (name) => {
    await updateGameTopic.mutateAsync({
      name,
      type: undefined,
      archived: undefined,
      communal: undefined,
    });
  };

  const toggleArchiveHandler = async () => {
    if (!topic) {
      return;
    }

    await archiveGameTopic.mutateAsync({
      name: undefined,
      type: undefined,
      archived: !topic.archived,
      communal: undefined,
    });

    await navigate(Path.game(gameId).topics(), { replace: true });
  };

  const toggleCommunalHandler = async () => {
    if (!topic) {
      return;
    }

    await updateGameTopic.mutateAsync({
      name: undefined,
      type: undefined,
      archived: undefined,
      communal: !topic.communal,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").TopicType} type
   */
  const updateType = async (type) => {
    await updateGameTopic.mutateAsync({
      name: undefined,
      type,
      archived: undefined,
      communal: undefined,
    });
  };

  const isOwner = profile && topic && profile.id === topic.lastModifiedByUserId;

  if (!topic) {
    return <Skeleton />;
  }

  return (
    <Box>
      <Card elevation={1} square={false}>
        {!hideTitle && (
          <Box display="flex">
            <Box flex={1}>
              {topic ? (
                <EditableCardHeader
                  text={customTitle || topic.name}
                  subheader={hideDate ? "" : topic ? formatTopicDate(topic.created) : <Skeleton />}
                  helperText=""
                  title=""
                  onSave={updateTitle}
                  disabled={!isOwner || !!customTitle || !!playTest}
                  onDisabledClick={playTest ? handleDisabledClick : undefined}
                />
              ) : (
                <Skeleton />
              )}
            </Box>
            <Stack p={1} direction="row" display="flex" flex={0} justifyContent="flex-start" alignItems="flex-start">
              <Tooltip title="Toggle whether this topic is locked so only the author can edit the first message.">
                <IconButton onClick={toggleCommunalHandler} disabled={!isOwner}>
                  {!topic.communal ? <Key /> : <KeyOff />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive">
                <IconButton onClick={toggleArchiveHandler}>
                  {archiveGameTopic.isPending ? <CircularProgress size={CircularProgressSize} /> : <Archive />}
                </IconButton>
              </Tooltip>
              {Features.PdfToRules && entitlements && entitlements.isMember && <PrintTopic topicId={topicId} />}
              <IconButton onClick={() => refetch()} style={{ justifyContent: "center", alignItems: "center" }}>
                {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Refresh />}
              </IconButton>
            </Stack>
          </Box>
        )}
        {!hideType && (
          <Box pl={2} pr={2}>
            {topic.type === "PlayTest" && (
              <ListItemText
                primary="Play Test"
                secondary={
                  <Typography variant="inherit" color="text.secondary">
                    This topic belongs to a play test.{" "}
                    {playTest && (
                      <AlwaysOnAppLink to={Path.game(gameId).test(playTest.id)}>Click to visit</AlwaysOnAppLink>
                    )}
                  </Typography>
                }
              />
            )}
            {topic.type === "Feedback" && (
              <ListItemText
                primary="Feedback"
                secondary={
                  <Typography variant="inherit" color="text.secondary">
                    This is for public feedback. You cannot change the type.
                  </Typography>
                }
              />
            )}
            {TopicTypes.includes(topic.type) && (
              <TopicTypeSelect
                disabled={!topic}
                value={topic.type}
                onChange={updateType}
                helperText="A broad category for this topic"
                isPending={updateGameTopic.isPending}
              />
            )}
          </Box>
        )}
        {topic && (
          <Box p={2}>
            <TopicTypeBanner type={topic.type} />
          </Box>
        )}
        <Box className="topic-print-content">
          {!hideTitle && (
            <Box className="print-only">
              <Typography variant="h5" component="h1" px={2} pt={2} pb={1}>
                {customTitle || topic.name}
              </Typography>
            </Box>
          )}
          <CardContent>
            {messages.map((message, i) => (
              <Message key={message.id} message={message} isFirst={i === 0} isCommunal={topic.communal} />
            ))}
          </CardContent>
        </Box>
      </Card>
      <Box p={1} />
      {messages.length > 0 && (
        <AddMessageToTopic
          implementation={messages[0].implementation}
          name={topic ? topic.name : ""}
          topicId={topicId}
        />
      )}
    </Box>
  );
};

export const TopicFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const topicId = useTopicIdFromRoute();

  return <TopicDisplay gameId={gameId} topicId={topicId} hideType={false} hideTitle={false} hideDate={false} />;
};
